import { type Scene, SceneKinds } from "./Soundboard";
import { type CardKind, CardKinds, PlaySettings, SoundCard } from "./SoundCard";
import { DEFAULT_SOUNDCARD_VOLUME, DEFAULT_SOUNDCARD_ENABLED, DEFAULT_SOUNDCARD_PLAY_SETTINGS } from './lib/constants';
import { v4 as uuidv4 } from 'uuid';
import { moveElementInArray } from "./global";
import { type SettingId, SettingsBank } from "./lib/Settings";
import { GuidedSceneSettingDescriptions} from "./GuidedSceneSettings";
import { RepeatSettings } from "./lib/repeat";
import GuidedSceneSettings from "./GuidedSceneSettings.svelte";

export class GuidedScene implements Scene {
  id: string;
  name: string;
  kind: string;
  bannerUrl: string;
  permalinkId: string;

  globalVolume: number;

  metadata: GuidedSceneMetadata;
  settings: SettingsBank<GuidedSceneSettings>;

  music: CardLane;
  ambiences: CardLane;
  effects: CardLane;

  constructor(rawScene: any) {
    this.id = rawScene.id;
    this.name = rawScene.name;
    this.kind = SceneKinds.Guided;
    this.bannerUrl = rawScene.bannerUrl;
    this.metadata = rawScene.metadata !== undefined && rawScene.metadata !== null ? GuidedSceneMetadata.fromRaw(rawScene.metadata) : null;
    this.permalinkId = rawScene.permalinkId !== undefined ? rawScene.permalinkId : this.id;

    if (rawScene.settings) {
      this.settings = new SettingsBank<GuidedSceneSettings>(rawScene.settings.values, GuidedSceneSettingDescriptions);
    } else {
      this.settings = SettingsBank.default<GuidedSceneSettings>(GuidedSceneSettingDescriptions);
    }

    this.music = new CardLane(rawScene.music ? rawScene.music : []);
    this.ambiences = new CardLane(rawScene.ambiences ? rawScene.ambiences : []);
    this.effects = new CardLane(rawScene.effects ? rawScene.effects : []);

    if (rawScene.globalVolume) {
      this.globalVolume = rawScene.globalVolume;
    } else {
      this.globalVolume = 100;
    }
  }
  RehashPermalinkIds() {
    this.permalinkId = uuidv4();
  }
  RehashIds() {
    this.id = uuidv4();
    this.music.RehashIds();
    this.ambiences.RehashIds();
    this.effects.RehashIds();
  }

  findContainingLane(cardId: string) {
    if (this.music.ContainsCard(cardId)) {
      return this.music;
    } else if (this.ambiences.ContainsCard(cardId)) {
      return this.ambiences;
    } else if (this.effects.ContainsCard(cardId)) {
      return this.effects;
    } else {
      return null;
    }
  }

  findLaneByKind(kind: CardKind): CardLane | null {
    if (kind === CardKinds.Music) {
      return this.music;
    } else if (kind === CardKinds.Ambience) {
      return this.ambiences;
    } else if (kind === CardKinds.Effect) {
      return this.effects;
    } else {
      return null;
    }
  }
  DeleteCard(cardId: string): void {
    const lane = this.findContainingLane(cardId);
    if (lane) {
      lane.DeleteCard(cardId);
    }
  }
  GetCard(cardId: string): SoundCard {
    const lane = this.findContainingLane(cardId);
    if (lane) {
      return lane.GetCard(cardId);
    }
    return null;
  }

  AddCard(title: string, rawSound: any, kind: CardKind) {
    const id = uuidv4();
    const card = SoundCard.fromRaw(title, id, rawSound, kind, DEFAULT_SOUNDCARD_PLAY_SETTINGS);
    card.asKind(kind);
    const lane = this.findLaneByKind(kind);
    lane.AddCard(card);
    return card;
  }

  AddCardAtIdx(title: string, sound: any, kind: CardKind, atIdx: number) {
    const lane = this.findLaneByKind(kind);
    const idx = lane.cards.length;
    this.AddCard(title, sound, kind);
    lane.MoveCard(idx, atIdx);
  }

  AddExistingCard(card: SoundCard, kind: CardKind, atIdx: number) {
    const lane = this.findLaneByKind(kind);
    const idx = lane.cards.length;
    card.asKind(kind);
    lane.AddCard(card);
    lane.MoveCard(idx, atIdx);
  }

  ContainsSound(soundId: string): boolean {
    return this.music.ContainsSound(soundId) || this.ambiences.ContainsSound(soundId) || this.effects.ContainsSound(soundId);
  }

  ChangeSettingValue<T>(settingId: SettingId, newValue: T) {
    this.settings.setValue(settingId, newValue);
  }

  Permalink(): string {
      return `${window.location.origin}/app/soundboard#${this.permalinkId}`
  }

  AllCards(): SoundCard[] {
      return this.ambiences.cards.concat(this.music.cards).concat(this.effects.cards);
  }
}
export class CardLane {
  cards: Array<SoundCard>
  indices: Map<string, number>

  constructor(rawCards) {
    this.cards = rawCards.cards ? rawCards.cards.map((rawCard) => {
      const volume = rawCard.volume !== undefined ? rawCard.volume : DEFAULT_SOUNDCARD_VOLUME;
      const enabled = rawCard.enabled !== undefined ? rawCard.enabled : DEFAULT_SOUNDCARD_ENABLED;
      const playSettings = rawCard.playSettings !== undefined ? rawCard.playSettings : {
        repeat: rawCard.kind === CardKinds.Ambience ? RepeatSettings.LOOP() : null,
        volume,
        enabled,
      };
      return SoundCard.fromRaw(rawCard.name, rawCard.id, rawCard.sound, rawCard.kind, playSettings);
    }) : [];
    this.indices = new Map(this.cards.map((card, idx) => [card.id, idx]))
  }
  ContainsCard(cardId: string): boolean {
    return this.indices.get(cardId) !== undefined;
  }
  ContainsSound(soundId: string): boolean {
    return this.cards.findIndex((card) => card.sound.id == soundId) !== -1;
  }
  DeleteCard(cardId: string): void {
    const index = this.indices.get(cardId);
    this.cards.splice(index, 1);
    this.indices.delete(cardId);
    this.updateCardIndices();
  }
  AddCard(card: SoundCard) {
    const idx = this.cards.length;
    this.cards.push(card);
    this.indices.set(card.id, idx);
  }
  GetCard(cardId: string) {
    return this.cards[this.indices.get(cardId)];
  }
  GetCardAtIdx(cardIdx: number): SoundCard {
    return this.cards[cardIdx];
  }
  RehashIds() {
    this.cards = this.cards.map((card) => {
      const newCard = card.clone();
      newCard.id = uuidv4();
      return newCard;
    })
    this.updateCardIndices();
  }
  MoveCard(oldIdx: number, newIdx: number) {
    moveElementInArray(this.cards, oldIdx, newIdx)
    this.updateCardIndices();
  }
  updateCardIndices() {
    this.indices = new Map();
    this.cards.forEach((card, idx) => {
      this.indices.set(card.id, idx)
    })
  }
}

export class GuidedSceneMetadata {
  iconUrl: string;
  iconTooltip: string;
  librarySummary: string;
  instructions: string;
  instructionsLink: string;

  private constructor(
    iconUrl: string,
    iconTooltip: string,
    librarySummary: string,
    instructions: string,
    instructionsLink: string,
  ) {
    this.iconUrl = iconUrl;
    this.iconTooltip = iconTooltip;
    this.librarySummary = librarySummary;
    this.instructions = instructions;
    this.instructionsLink = instructionsLink;
  }

  public static fromRaw(rawMetadata: any): GuidedSceneMetadata {
    return new GuidedSceneMetadata(
      rawMetadata.iconUrl !== undefined ? rawMetadata.iconUrl : null,
      rawMetadata.iconTooltip !== undefined ? rawMetadata.iconTooltip : null,
      rawMetadata.librarySummary !== undefined ? rawMetadata.librarySummary : null,
      rawMetadata.instructions !== undefined ? rawMetadata.instructions : null,
      rawMetadata.instructionsLink !== undefined ? rawMetadata.instructionsLink : null,
    )
  }
}
